import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, apiJson, assetUrl } from "../../utils/api";
import { Disclaimer } from "./MarriageLayout";
import { fetchCoupleByUser, getCoupleCode, getUserId, setCoupleCode } from "../../utils/coupleSession";
import useSeo from "../../hooks/useSeo";

export default function MarriageSimulation() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    male_name: "",
    female_name: "",
    love_since: "",
    city: "",
    ready_marry: "考虑中",
    ready_house: "还没想好",
    ready_child: "还没想好",
  });
  const [couple, setCouple] = useState(null);
  const [codeInput, setCodeInput] = useState(getCoupleCode());
  const [error, setError] = useState("");

  useSeo({
    title: "模拟领证 | 我们结婚吧",
    metaDescription: "互动体验模拟婚姻登记并生成电子纪念证，不具有法律效力。",
    path: "/marriage/simulation",
  });

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const persist = (data) => {
    setCouple(data);
    if (data?.code) {
      setCoupleCode(data.code);
      setCodeInput(data.code);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const userId = getUserId();
      const { res, data } = await apiJson("/api/marriage/simulations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, user_id: userId || null }),
      });
      if (!res.ok) throw new Error(data?.detail || "创建失败");
      persist(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const loadCode = async () => {
    if (!codeInput) return;
    try {
      const { res, data } = await apiJson(`/api/marriage/simulations/${codeInput}`);
      if (!res.ok) throw new Error(data?.detail || "未找到");
      persist(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const vow = async (side) => {
    const { res, data } = await apiJson(`/api/marriage/simulations/${couple.code}/vow`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ side }),
    });
    if (res.ok) persist(data);
  };

  const uploadPhoto = async (kind, file) => {
    if (!file || !couple?.code) return;
    const body = new FormData();
    body.append("kind", kind);
    body.append("file", file);
    const res = await apiFetch(`/api/marriage/simulations/${couple.code}/photos`, {
      method: "POST",
      body,
    });
    const data = await res.json();
    if (res.ok) persist(data);
    else setError(data?.detail || "图片上传失败");
  };

  useEffect(() => {
    const boot = async () => {
      if (codeInput) {
        await loadCode();
        return;
      }
      const userId = getUserId();
      if (!userId) return;
      const existing = await fetchCoupleByUser(userId);
      if (existing) {
        persist(existing);
        return;
      }
      try {
        const { data } = await apiJson(`/api/profile/${userId}`);
        if (!data) return;
        setForm((prev) => {
          const name = (data.name || "").trim();
          const city = prev.city || data.city || data.province || "";
          if ((data.gender || "").trim() === "女") {
            return { ...prev, female_name: prev.female_name || name, city };
          }
          return { ...prev, male_name: prev.male_name || name, city };
        });
      } catch {
        /* 未登录或资料不完整时保持空白，让用户手填昵称 */
      }
    };
    boot();
  }, []);

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-3xl font-extrabold">❤️ 我们结婚吧</h1>
      <Disclaimer />
      <div className="flex gap-2">
        <input
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
          placeholder="已有纪念码？"
          className="border rounded-xl px-3 py-2 flex-1"
        />
        <button type="button" onClick={loadCode} className="px-4 py-2 rounded-xl border">
          进入
        </button>
      </div>
      {error ? <p className="text-red-500 text-sm">{error}</p> : null}

      {!couple ? (
        <form onSubmit={create} className="bg-white rounded-2xl p-5 grid sm:grid-cols-2 gap-3">
          <input required placeholder="男方昵称" className="border rounded-xl px-3 py-2" value={form.male_name} onChange={(e) => setField("male_name", e.target.value)} />
          <input required placeholder="女方昵称" className="border rounded-xl px-3 py-2" value={form.female_name} onChange={(e) => setField("female_name", e.target.value)} />
          <input placeholder="恋爱时间，如 2023-05-20" className="border rounded-xl px-3 py-2" value={form.love_since} onChange={(e) => setField("love_since", e.target.value)} />
          <input placeholder="所在城市" className="border rounded-xl px-3 py-2" value={form.city} onChange={(e) => setField("city", e.target.value)} />
          <select className="border rounded-xl px-3 py-2" value={form.ready_marry} onChange={(e) => setField("ready_marry", e.target.value)}>
            <option>准备结婚</option>
            <option>考虑中</option>
            <option>先体验一下</option>
          </select>
          <select className="border rounded-xl px-3 py-2" value={form.ready_house} onChange={(e) => setField("ready_house", e.target.value)}>
            <option>准备买房</option>
            <option>暂不买房</option>
            <option>还没想好</option>
          </select>
          <select className="border rounded-xl px-3 py-2 sm:col-span-2" value={form.ready_child} onChange={(e) => setField("ready_child", e.target.value)}>
            <option>准备生育</option>
            <option>暂不考虑</option>
            <option>还没想好</option>
          </select>
          <p className="text-sm text-gray-500 sm:col-span-2">
            男方/女方昵称会写进纪念码。登录后会带入你在网站资料里的姓名和城市；另一半用这串码进入同一场模拟。
          </p>
          <button className="sm:col-span-2 py-3 rounded-xl bg-rose-500 text-white font-medium">进入模拟婚姻登记中心</button>
        </form>
      ) : (
        <div className="bg-white rounded-3xl p-8 text-center space-y-6 border-4 border-rose-100">
          <p className="text-sm text-gray-400">欢迎来到模拟婚姻登记中心</p>
          <p className="font-mono text-rose-500">纪念码 {couple.code} · 可分享给另一半</p>
          <div className="grid sm:grid-cols-3 gap-3 text-left text-sm">
            {[
              ["male_avatar", "男方头像"],
              ["female_avatar", "女方头像"],
              ["couple_photo", "二人合影"],
            ].map(([kind, label]) => (
              <label key={kind} className="border rounded-xl p-3 block">
                <span className="text-gray-500">{label}</span>
                {couple[kind] ? (
                  <img src={assetUrl(couple[kind])} alt={label} className="w-full h-24 object-cover rounded-lg mt-2" />
                ) : (
                  <div className="h-24 mt-2 bg-rose-50 rounded-lg flex items-center justify-center text-gray-400">未上传</div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="mt-2 text-xs"
                  onChange={(e) => uploadPhoto(kind, e.target.files?.[0])}
                />
              </label>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <button
              disabled={couple.vow_male}
              onClick={() => vow("male")}
              className={`rounded-2xl py-8 font-bold ${couple.vow_male ? "bg-rose-100 text-rose-700" : "bg-rose-500 text-white"}`}
            >
              男方 {couple.male_name}
              <div className="text-2xl mt-2">{couple.vow_male ? "已说：我愿意" : "我愿意"}</div>
            </button>
            <button
              disabled={couple.vow_female}
              onClick={() => vow("female")}
              className={`rounded-2xl py-8 font-bold ${couple.vow_female ? "bg-pink-100 text-pink-700" : "bg-pink-500 text-white"}`}
            >
              女方 {couple.female_name}
              <div className="text-2xl mt-2">{couple.vow_female ? "已说：我愿意" : "我愿意"}</div>
            </button>
          </div>
          {couple.simulation?.status === "completed" ? (
            <div className="space-y-3">
              <p className="text-xl font-bold text-rose-600">恭喜二位完成模拟结婚登记！</p>
              <button
                onClick={() => navigate(`/marriage/certificate/${couple.code}`)}
                className="px-6 py-3 rounded-full bg-amber-500 text-white"
              >
                查看《模拟结婚纪念证》
              </button>
            </div>
          ) : (
            <p className="text-gray-500">请双方分别点击「我愿意」</p>
          )}
        </div>
      )}
    </div>
  );
}
